import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, BarChart2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const money = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;
const moneyShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `R$${(n / 1000).toFixed(1).replace('.', ',')}k`;
  return `R$${n.toFixed(0)}`;
};

// Modo "status": Pago + A Pagar (empilhado)
const STATUS_SEGMENTS = [
  { key: 'total_pago',    label: 'Pago',    bar: 'bg-emerald-500/80', hover: 'bg-emerald-400' },
  { key: 'total_a_pagar', label: 'A pagar', bar: 'bg-amber-500/80',   hover: 'bg-amber-400' },
];

// Modo "categoria": 5 categorias (empilhado). Cores alinhadas com FinanceDashboard.js
const CATEGORY_SEGMENTS = [
  { key: 'dublagem', label: 'Dublagem', bar: 'bg-teal-500/80',    hover: 'bg-teal-400' },
  { key: 'gerencia', label: 'Gerência', bar: 'bg-purple-500/80',  hover: 'bg-purple-400' },
  { key: 'mixagem',  label: 'Mixagem',  bar: 'bg-blue-500/80',    hover: 'bg-blue-400' },
  { key: 'roteiro',  label: 'Roteiro',  bar: 'bg-amber-500/80',   hover: 'bg-amber-400' },
  { key: 'srt',      label: 'SRT',      bar: 'bg-pink-500/80',    hover: 'bg-pink-400' },
];

const getValue = (h, seg) => {
  if (seg.key in (h.categories || {})) return h.categories[seg.key] || 0;
  return h[seg.key] || 0;
};

const HistoryChart = ({ months = 12 }) => {
  const { getAuthHeaders } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const [mode, setMode] = useState('status'); // 'status' | 'categoria'

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/finance-dashboard/history?months=${months}`, { headers: getAuthHeaders() })
      .then(res => setHistory(res.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [months, getAuthHeaders]);

  const segments = mode === 'categoria' ? CATEGORY_SEGMENTS : STATUS_SEGMENTS;

  const maxValue = useMemo(
    () => Math.max(1, ...history.map(h => h.total)),
    [history]
  );

  const totalPeriod = useMemo(
    () => history.reduce((acc, h) => acc + h.total, 0),
    [history]
  );

  return (
    <section className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="history-chart">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <LineChart className="h-5 w-5 text-cyan-400" />
          <div>
            <h2 className="text-xl font-semibold text-white">Histórico — Últimos {months} meses</h2>
            <p className="text-xs text-slate-400">
              {mode === 'categoria' ? 'Distribuição por categoria de serviço' : 'Pago vs. A Pagar'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Mode switch */}
          <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-700 overflow-hidden" data-testid="history-mode-switch">
            <button
              type="button"
              onClick={() => setMode('status')}
              className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                mode === 'status' ? 'text-emerald-300 bg-emerald-500/20' : 'text-slate-400 hover:text-slate-200'
              }`}
              data-testid="history-mode-status"
            >
              <LineChart className="h-3.5 w-3.5" /> Por Status
            </button>
            <button
              type="button"
              onClick={() => setMode('categoria')}
              className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                mode === 'categoria' ? 'text-teal-300 bg-teal-500/20' : 'text-slate-400 hover:text-slate-200'
              }`}
              data-testid="history-mode-categoria"
            >
              <BarChart2 className="h-3.5 w-3.5" /> Por Categoria
            </button>
          </div>

          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-slate-400">Total do período</p>
            <p className="text-xl font-mono font-bold text-white">{money(totalPeriod)}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-cyan-400" />
        </div>
      ) : history.length === 0 ? (
        <p className="text-slate-400 text-sm text-center py-6">Sem histórico disponível.</p>
      ) : (
        <>
          <div className="flex items-end gap-1 md:gap-2 h-64 pt-4 pb-2 relative" data-testid="history-bars">
            {history.map((h) => {
              const totalH = (h.total / maxValue) * 100;
              const key = `${h.year}-${h.month}`;
              const isHover = hover === key;
              return (
                <div
                  key={key}
                  className="flex-1 flex flex-col items-center justify-end h-full min-w-0 cursor-pointer relative"
                  onMouseEnter={() => setHover(key)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`history-bar-${h.year}-${h.month}`}
                >
                  {/* Tooltip */}
                  {isHover && h.total > 0 && (
                    <div className="absolute bottom-full mb-2 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 shadow-xl text-xs z-10 pointer-events-none whitespace-nowrap">
                      <p className="text-white font-semibold">{MONTHS_SHORT[h.month - 1]}/{h.year}</p>
                      {segments.map(seg => {
                        const v = getValue(h, seg);
                        if (v <= 0) return null;
                        return (
                          <p key={seg.key} className={`${seg.bar.replace('bg-', 'text-').replace('/80', '')}`}>
                            {seg.label}: {money(v)}
                          </p>
                        );
                      })}
                      <p className="text-slate-300 pt-1 border-t border-slate-700 mt-1">Total: {money(h.total)}</p>
                    </div>
                  )}

                  {h.total > 0 && (
                    <span className="text-[9px] md:text-[10px] font-mono text-slate-400 mb-1 whitespace-nowrap">
                      {moneyShort(h.total)}
                    </span>
                  )}

                  {/* Stack */}
                  <div
                    className="w-full flex flex-col justify-end"
                    style={{ height: `${totalH}%`, minHeight: h.total > 0 ? '2px' : '0' }}
                  >
                    {segments.map((seg, idx) => {
                      const v = getValue(h, seg);
                      if (v <= 0) return null;
                      const flex = (v / h.total) * 100;
                      const isFirstVisible = segments.slice(0, idx).every(s => getValue(h, s) <= 0);
                      return (
                        <div
                          key={seg.key}
                          className={`w-full transition-colors ${isHover ? seg.hover : seg.bar} ${isFirstVisible ? 'rounded-t-sm' : ''}`}
                          style={{ flex, minHeight: '1px' }}
                        />
                      );
                    })}
                  </div>

                  <div className="mt-1 text-center">
                    <span className="block text-[10px] md:text-xs text-slate-400">
                      {MONTHS_SHORT[h.month - 1]}
                    </span>
                    <span className="block text-[9px] text-slate-600">
                      {String(h.year).slice(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legenda dinâmica */}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs flex-wrap">
            {segments.map(seg => (
              <div key={seg.key} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm ${seg.bar}`} />
                <span className="text-slate-400">{seg.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HistoryChart;
